/**
 * Environment configuration entry point
 *
 * This file serves as the main entry point for environment configuration.
 * It only exports types and client-safe functionality to ensure proper code splitting.
 */

// Export environment schema for type usage
export * from './schema';

import type { EnvSchema } from './schema';

/**
 * Get client-side environment variables
 * This function is safe to use in both client and server code
 */
export function getClientEnv(): EnvSchema['client'] {
  if (typeof window !== 'undefined') {
    // Browser environment - get ENV from window object
    const env = (window as any).ENV;
    if (!env) {
      //  logger.warn('Environment variables not found on window.ENV');
      return {} as EnvSchema['client'];
    }
    return env;
  } else {
    // On the server, we don't need to return client env here
    // This pattern ensures clean code splitting between server/client
    return {} as EnvSchema['client'];
  }
}

/**
 * Safely creates an environment script element for client use
 * This function is automatically eliminated from client bundles
 */
export function createEnvScript(clientEnv: EnvSchema['client']): { __html: string } {
  if (typeof window !== 'undefined') {
    // This should never run on the client, but we handle it gracefully
    //logger.warn('createEnvScript should only be called on the server');
    return { __html: '' };
  }

  // Safely stringify the environment object with error handling
  try {
    return {
      __html: `window.ENV = ${JSON.stringify(clientEnv || {})}`,
    };
  } catch (e) {
    //logger.error('Failed to serialize client environment:', e);
    return { __html: 'window.ENV = {}' };
  }
}
